import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import { run, stopRunningScript } from "../../utils/runScript";
import textToSpeechManager from "../../utils/TextToSpeechManager";
import { findStartBlockIds } from "../../utils/scriptStarts";
import "../../css/RightPanelControls.css";
import "../../css/SimulatorView.css";
import {
  cycleSimulatorBackground,
  getCurrentSimulatorBackground,
  uploadSimulatorBackground,
  saveProjectFromSimulator,
  loadProjectFromSimulator,
  cycleSimulatorRobotType
} from "../../utils/runScript";
import { setSimulatorOpen as setSimulatorOpenAction, setDrawLineSettings } from "../../store/sceneSlice";
import { STAGE_CELL_SIZE } from "../../constants/stageDimensions";


const BackgroundGallery = React.lazy(() => import("./BackgroundGallery"));
const SimulatorModal = React.lazy(() => import("../../SimulatorView/SimulatorModal"));
const ObstacleGallery = React.lazy(() => import("../../editor/ui/ObstacleGallery"));
const PaintEditor = React.lazy(() => import("../../painteditor/PaintEditor")); // ADD THIS LINE
const ColoredAreaGallery = React.lazy(() => import("../../editor/ui/ColoredAreaGallery"));

const BUILT_IN_BACKGROUNDS = [
  './assets/backgrounds/bg1.svg',
  './assets/backgrounds/bg2.svg',
  './assets/backgrounds/bg3.svg',
  './assets/backgrounds/bg4.svg',
  './assets/backgrounds/bg5.svg'
];

const RECORDING_FPS = 24;
const RECORDING_VIDEO_BITRATE = 2500000;
const RECORDING_AUDIO_BITRATE = 128000;

const sanitizeRecordingName = (value) => {
  const fallback = "Blockzie_JR_Screen_Recording";
  if (!value) return fallback;
  const trimmed = String(value).trim();
  if (!trimmed) return fallback;
  const cleaned = trimmed.replace(/[^\w.-]+/g, "_").slice(0, 120);
  return cleaned || fallback;
};

const getImageCacheEntry = (cache, src) => {
  if (!src) return null;
  const existing = cache.get(src);
  if (existing) return existing;
  const img = new Image();
  img.crossOrigin = "anonymous";
  const entry = { img, loaded: false, error: false };
  img.onload = () => {
    entry.loaded = true;
  };
  img.onerror = () => {

    entry.error = true;
  };
  img.src = src;
  cache.set(src, entry);
  return entry;
};

const drawImageContain = (ctx, img, width, height) => {
  if (!img || !img.width || !img.height) return;
  const scale = Math.min(width / img.width, height / img.height);
  const drawW = img.width * scale;
  const drawH = img.height * scale;
  const x = (width - drawW) / 2;
  const y = (height - drawH) / 2;
  ctx.drawImage(img, x, y, drawW, drawH);
};

const drawRoundedRect = (ctx, x, y, w, h, r) => {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
};

// Draw Line Configuration Modal Component (logic-only tweaks)
function DrawLineModal({ onClose, dispatch }) {
  const [lineSettings, setLineSettings] = useState(() => {
    try {
      const stored = localStorage.getItem("simulatorDrawLineSettings");
      return stored
        ? JSON.parse(stored)
        : { enabled: false, thickness: 3, size: 10, color: "#FF0000" };
    } catch (error) {
      return { enabled: false, thickness: 3, size: 10, color: "#FF0000" };
    }
  });

  const handleApplySettings = () => {
    try {
      localStorage.setItem(
        "simulatorDrawLineSettings",
        JSON.stringify(lineSettings)
      );
      window.dispatchEvent(
        new CustomEvent("drawLineSettingsChanged", {
          detail: lineSettings
        })
      );
      dispatch(setDrawLineSettings(lineSettings));
      console.log("✅ Draw line settings applied:", lineSettings);
      onClose();
    } catch (error) {
      console.error("Error applying settings:", error);
    }
  };

  const handleToggleDrawing = () => {
    try {
      const newSettings = { ...lineSettings, enabled: !lineSettings.enabled };
      setLineSettings(newSettings);
      localStorage.setItem(
        "simulatorDrawLineSettings",
        JSON.stringify(newSettings)
      );
      window.dispatchEvent(
        new CustomEvent("drawLineSettingsChanged", {
          detail: newSettings
        })
      );
      dispatch(setDrawLineSettings(newSettings));
    } catch (error) {
      console.error("Error toggling drawing:", error);
    }
  };

  const modalStyles = {
    modalBackdrop: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      background: "rgba(0, 0, 0, 0.7)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 2000
    },
    modal: {
      background: "white",
      borderRadius: "12px",
      padding: "20px",
      maxWidth: "400px",
      width: "90%",
      maxHeight: "80vh",
      overflow: "auto",
      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
      zIndex: 2001
    }
  };

  return (
    <div style={modalStyles.modalBackdrop} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px"
          }}
        >
          <h3 style={{ margin: 0, color: "#333", fontSize: "18px" }}>
            ✏️ Draw Line Configuration
          </h3>
          <button
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer"
            }}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <label
              style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}
            >
              <input
                type="checkbox"
                checked={lineSettings.enabled}
                onChange={handleToggleDrawing}
                style={{ width: "20px", height: "20px", cursor: "pointer" }}
              />
              <span style={{ fontSize: "16px", fontWeight: 600, color: "#333" }}>
                Enable Drawing Trail
              </span>
            </label>
          </div>

          <div>
            <label style={{ fontWeight: 500, color: "#333", fontSize: "14px" }}>
              Line Thickness: {lineSettings.thickness}px
            </label>
            <input
              type="range"
              min="1"
              max="10"
              value={lineSettings.thickness}
              onChange={(e) =>
                setLineSettings({
                  ...lineSettings,
                  thickness: parseInt(e.target.value, 10)
                })
              }
              style={{ width: "100%" }}
            />
          </div>

          <div>
            <label style={{ fontWeight: 500, color: "#333", fontSize: "14px" }}>
              Line Color:
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <input
                type="color"
                value={lineSettings.color}
                onChange={(e) =>
                  setLineSettings({
                    ...lineSettings,
                    color: e.target.value
                  })
                }
                style={{
                  width: "50px",
                  height: "35px",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer"
                }}
              />
            </div>
          </div>
        </div>

        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}
        >
          <button
            style={{
              padding: "10px 20px",
              backgroundColor: "#4A90E2",
              color: "black",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer"
            }}
            onClick={handleApplySettings}
          >
            Apply Settings
          </button>
          <button
            style={{
              padding: "10px 20px",
              backgroundColor: "#6c757d",
              color: "black",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer"
            }}
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function RecordingOptionsModal({ open, onConfirm, onCancel, disabled }) {
  const [mode, setMode] = useState("web");

  useEffect(() => {
    if (open) setMode("web");
  }, [open]);

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2200
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          padding: "20px",
          width: "90%",
          maxWidth: "420px",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 12px", color: "#222", fontSize: "18px" }}>
          Recording Audio
        </h3>
        <p style={{ margin: "0 0 12px", color: "#444", fontSize: "13px" }}>
          Choose what audio to capture. Microphone permission may be requested if you include surroundings.
        </p>
        <label style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
          <input
            type="radio"
            name="record-audio"
            value="web"
            checked={mode === "web"}
            onChange={() => setMode("web")}
          />
          <span style={{ fontSize: "14px", color: "#222" }}>Project audio only</span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <input
            type="radio"
            name="record-audio"
            value="both"
            checked={mode === "both"}
            onChange={() => setMode("both")}
          />
          <span style={{ fontSize: "14px", color: "#222" }}>Project + surroundings audio</span>
        </label>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "18px" }}>
          <button
            style={{
              padding: "8px 16px",
              border: "none",
              borderRadius: "6px",
              background: "#6c757d",
              color: "black",
              cursor: "pointer"
            }}
            onClick={onCancel}
            disabled={disabled}
          >
            Cancel
          </button>
          <button
            style={{
              padding: "8px 16px",
              border: "none",
              borderRadius: "6px",
              background: "#4A90E2",
              color: "black",
              cursor: "pointer"
            }}
            onClick={() => onConfirm({ includeMic: mode === "both" })}
            disabled={disabled}
          >
            Start Recording
          </button>
        </div>
      </div>
    </div>
  );
}

function RecordingSaveModal({ open, name, onNameChange, onSave, onDiscard, saving }) {
  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2200
      }}
      onClick={onDiscard}
    >
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          padding: "20px",
          width: "90%",
          maxWidth: "420px",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 12px", color: "#222", fontSize: "18px" }}>
          Recording Ready
        </h3>
        <p style={{ margin: "0 0 12px", color: "#444", fontSize: "13px" }}>
          Choose a file name and save the recording to your device.
        </p>
        <input
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 10px",
            borderRadius: "6px",
            border: "1px solid #ccc",
            fontSize: "14px",
            marginBottom: "12px"
          }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <button
            style={{
              padding: "8px 16px",
              border: "none",
              borderRadius: "6px",
              background: "#6c757d",
              color: "black",
              cursor: "pointer"
            }}
            onClick={onDiscard}
            disabled={saving}
          >
            Discard
          </button>
          <button
            style={{
              padding: "8px 16px",
              border: "none",
              borderRadius: "6px",
              background: "#4A90E2",
              color: "black",
              cursor: "pointer"
            }}
            onClick={onSave}
            disabled={saving}
          >
            Save Recording
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RightPanelControls({
  onFullScreen,
  onGridToggle,
  onHeading,
  onGreenFlag,
  selectedActorId,
  onSimulator,
  onBackgroundChange
}) {
  const dispatch = useDispatch();
  const {
    scenes,
    currentSceneIndex,
    simulatorRobots,
    selectedSimRobotId,
    simulatorOpen,
    simGridWidth,
    simGridHeight
  } = useSelector((s) => s.scene);
  const store = useStore();
  const getState = store.getState;
  const selectedSimRobot =
    (simulatorRobots || []).find((r) => r.id === selectedSimRobotId) || null;

  const scene = scenes[currentSceneIndex];
  const stageActor = scene?.actors.find((a) => a.id === selectedActorId);
  const simGridW = Math.max(1, Math.floor(simGridWidth ?? 20));
  const simGridH = Math.max(1, Math.floor(simGridHeight ?? 15));


  const [backgroundModalOpen, setBackgroundModalOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [stageRect, setStageRect] = useState(null);

  const [showObstacleGallery, setShowObstacleGallery] = useState(false);
  const [showColoredAreaGallery, setShowColoredAreaGallery] = useState(false);
  const [showDrawLineModal, setShowDrawLineModal] = useState(false);
  const [showPaintEditor, setShowPaintEditor] = useState(false); // ADD THIS LINE
  const [customBackgrounds, setCustomBackgrounds] = useState([]);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [showRecordingOptions, setShowRecordingOptions] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingRecordingBlob, setPendingRecordingBlob] = useState(null);
  const [pendingRecordingName, setPendingRecordingName] = useState("");
  const [showRecordingSave, setShowRecordingSave] = useState(false);
  const [showViewDropdown, setShowViewDropdown] = useState(false);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const micStreamRef = useRef(null);
  const canvasRef = useRef(null);
  const canvasStreamRef = useRef(null);
  const captureTrackRef = useRef(null);
  const canvasHostRef = useRef(null);
  const renderLoopRef = useRef(null);
  const lastFrameTimeRef = useRef(0);
  const imageCacheRef = useRef(new Map());
  const audioContextRef = useRef(null);
  const recordingAudioDestinationRef = useRef(null);
  const recordingTargetRef = useRef(null);
  const recordingModeRef = useRef("stage");
  const recordSizeRef = useRef(null);
  const stopRequestedRef = useRef(false);
  const isRecordingRef = useRef(false);
  const isUnmountingRef = useRef(false);
  const stopHandledRef = useRef(false);
  const stopFallbackTimerRef = useRef(null);

  const allBackgrounds = useMemo(
    () => [...BUILT_IN_BACKGROUNDS, ...customBackgrounds],
    [customBackgrounds]
  );

  const handleGreenFlagClick = async () => {
    try {
      textToSpeechManager.unlockAudio();
      const currentState = store.getState();
      const sceneState = currentState.scene;
      const currentScene = sceneState?.scenes?.[sceneState?.currentSceneIndex];
      const isSimulatorVisible = document.querySelector(".simulator-robot") !== null;
      const simRobots = sceneState?.simulatorRobots || [];
      const currentSelectedSimRobot = simRobots.find((r) => r.id === sceneState?.selectedSimRobotId) || null;
      const currentStageActor = currentScene?.actors?.find((a) => a.id === selectedActorId);

      if (isSimulatorVisible && simRobots.length > 0) {
        if (currentSelectedSimRobot) {
          const greenIds = findStartBlockIds(
            currentSelectedSimRobot.scripts,
            (block) => block.name === "Start on Green Flag"
          );
          if (greenIds.length) {
            console.log(
              "Running selected simulator robot green-flag scripts:",
              currentSelectedSimRobot.name
            );
            await run(
              currentSelectedSimRobot,
              dispatch,
              currentScene?.sounds,
              currentSelectedSimRobot.id,
              { startBlockIds: greenIds }
            );
          } else {
            console.warn("Selected simulator robot has no green-flag scripts:", currentSelectedSimRobot);
          }
        } else {
          for (const robot of simRobots) {
            const greenIds = findStartBlockIds(robot.scripts, (block) => block.name === "Start on Green Flag");
            if (!greenIds.length) {
              console.warn("Simulator robot has no green-flag scripts to run:", robot.name);
              continue;
            }
            console.log("Running simulator robot green-flag scripts:", robot.name);
            await run(robot, dispatch, currentScene?.sounds, robot.id, { startBlockIds: greenIds });
          }
        }
      } else if (currentStageActor) {
        const greenIds = findStartBlockIds(currentStageActor.scripts, (block) => block.name === "Start on Green Flag");
        if (!greenIds.length) {
          console.warn("Stage actor has no green-flag scripts to run.");
          return;
        }
        console.log("Running stage actor green-flag scripts:", currentStageActor.name);
        await run(currentStageActor, dispatch, currentScene?.sounds, currentStageActor.id, { startBlockIds: greenIds });
      } else {
        console.log("No actor or robot available to run");
      }
    } catch (err) {
      console.error("Error while running script:", err);
    }
  };
  const handleSimulatorBackgroundSelection = () => {
    console.log('Background selection clicked - cycling simulator background');

    // Cycle to next background
    const nextIndex = (currentBgIndex + 1) % allBackgrounds.length;
    setCurrentBgIndex(nextIndex);

    const nextBg = allBackgrounds[nextIndex];

    // Set as current background
    localStorage.setItem('simulatorBackground', nextBg);

    // Trigger background changed event
    window.dispatchEvent(new Event('simulatorBackgroundChanged'));

    console.log(`Cycled to background ${nextIndex + 1}/${allBackgrounds.length}`);
  };

  const handleRobotSelection = () => {
    cycleSimulatorRobotType(dispatch);
  };

  const handleDrawTailLine = () => {
    setShowDrawLineModal(true);
  };

  const handleUploadBackground = async () => {
    const success = await uploadSimulatorBackground();
    console.log(success ? "✅ Background uploaded successfully" : "❌ Background upload failed or cancelled");
  };

  const handleSaveProject = () => {
    saveProjectFromSimulator(dispatch, getState);
  };

  const handleLoadProject = async () => {
    await loadProjectFromSimulator(dispatch);
  };

  const handleObstacle = () => {
    setShowObstacleGallery(true);
  };

  const handleColoredArea = () => {
    setShowColoredAreaGallery(true);
  };

  // ADD THESE NEW FUNCTIONS:
  const handlePaintBackground = () => {
    console.log('Paint background clicked');
    setShowPaintEditor(true);
  };

  const handlePaintSave = (imageDataURL) => {
    console.log('✅ Paint saved - adding to background cycle');

    localStorage.setItem('simulatorBackground', imageDataURL);

    setCustomBackgrounds(prev => {
      const updated = [...prev, imageDataURL];
      const nextIndex = BUILT_IN_BACKGROUNDS.length + updated.length - 1;
      setCurrentBgIndex(nextIndex);
      window.dispatchEvent(new Event('simulatorBackgroundChanged'));
      console.log(`Custom background added! Total backgrounds: ${BUILT_IN_BACKGROUNDS.length + updated.length}`);
      return updated;
    });

    // Close paint editor
    setShowPaintEditor(false);
  };

  const handleDeleteCustomBackground = (index) => {
    setCustomBackgrounds(prev => prev.filter((_, i) => i !== index));
    console.log('Custom background deleted');
  };

  const getRecordingTarget = () => {
    if (simulatorOpen) {
      return (
        document.querySelector(".simulator-stage-wrapper .stage-area-section") ||
        document.querySelector(".simulator-stage-wrapper")
      );
    }
    return document.querySelector("#stage-area");
  };

  const stopTracks = (stream) => {
    if (!stream) return;
    stream.getTracks().forEach((track) => track.stop());
  };

  const cleanupRecording = () => {
    if (renderLoopRef.current) {
      cancelAnimationFrame(renderLoopRef.current);
    }
    renderLoopRef.current = null;
    lastFrameTimeRef.current = 0;
    stopTracks(micStreamRef.current);
    micStreamRef.current = null;

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => { });
    }

    recorderRef.current = null;
    canvasRef.current = null;
    canvasStreamRef.current = null;
    captureTrackRef.current = null;
    audioContextRef.current = null;
    recordingAudioDestinationRef.current = null;
    recordingTargetRef.current = null;
    recordingModeRef.current = "stage";
    recordSizeRef.current = null;
    stopRequestedRef.current = false;
    isRecordingRef.current = false;
    if (window.__blockzieRecordingAudio) {
      window.__blockzieRecordingAudio = null;
    }
    if (window.__blockzieRecordingActive) {
      window.__blockzieRecordingActive = false;
    }
    if (window.__blockzieRecordingDrawFrame) {
      window.__blockzieRecordingDrawFrame = null;
    }
    if (window.__blockzieRecordingAttachAudio) {
      window.__blockzieRecordingAttachAudio = null;
    }
    if (stopFallbackTimerRef.current) {
      clearTimeout(stopFallbackTimerRef.current);
      stopFallbackTimerRef.current = null;
    }
    if (canvasHostRef.current) {
      canvasHostRef.current.remove();
      canvasHostRef.current = null;
    }
  };

  const saveWebmFile = async (blob, baseName) => {
    const fileName = `${baseName}.webm`;
    if (window.showSaveFilePicker) {
      const handle = await window.showSaveFilePicker({
        suggestedName: fileName,
        types: [
          {
            description: "WebM Video",
            accept: { "video/webm": [".webm"] }
          }
        ]
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const drawStageFrame = (ctx, width, height) => {
    const sceneState = store.getState().scene;
    const currentScene = sceneState?.scenes?.[sceneState.currentSceneIndex];
    if (!currentScene) return;

    const bg = currentScene.background || "#ffffff";
    ctx.clearRect(0, 0, width, height);

    if (bg.startsWith("#")) {
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);
    } else {
      const entry = getImageCacheEntry(imageCacheRef.current, bg);
      if (entry?.loaded && !entry.error) {
        drawImageContain(ctx, entry.img, width, height);
      } else {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);
      }
    }

    const drawLineSettings = sceneState.drawLineSettings || {};
    const actorTrails = currentScene.actorTrails || {};
    if (drawLineSettings.enabled) {
      ctx.strokeStyle = drawLineSettings.color || "#ff0000";
      ctx.lineWidth = drawLineSettings.thickness || 3;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      Object.values(actorTrails).forEach((points) => {
        if (!Array.isArray(points) || points.length < 2) return;
        ctx.beginPath();
        points.forEach((pt, idx) => {
          const px = (pt.x + 0.5) * STAGE_CELL_SIZE;
          const py = (pt.y + 0.5) * STAGE_CELL_SIZE;
          if (idx === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        });
        ctx.stroke();
      });
    }

    const obstacles = currentScene.obstacles || [];
    obstacles.forEach((obstacle) => {
      const size = STAGE_CELL_SIZE * 2;
      const x = (obstacle.x + 0.5) * STAGE_CELL_SIZE;
      const y = (obstacle.y + 0.5) * STAGE_CELL_SIZE;
      ctx.save();
      ctx.translate(x, y);
      ctx.fillStyle = obstacle.color || "#4a90e2";
      if (obstacle.shape === "circle") {
        ctx.beginPath();
        ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (obstacle.shape === "triangle") {
        ctx.beginPath();
        ctx.moveTo(0, -size / 2);
        ctx.lineTo(size / 2, size / 2);
        ctx.lineTo(-size / 2, size / 2);
        ctx.closePath();
        ctx.fill();
      } else {
        ctx.fillRect(-size / 2, -size / 2, size, size);
      }
      ctx.restore();
    });

    const actors = currentScene.actors || [];
    actors.forEach((actor) => {
      if (actor.visible === false) return;
      const size = STAGE_CELL_SIZE * 4 * (actor.size || 1);
      const x = (actor.x + 0.5) * STAGE_CELL_SIZE;
      const y = (actor.y + 0.5) * STAGE_CELL_SIZE;
      const entry = getImageCacheEntry(imageCacheRef.current, actor.image);
      if (entry?.loaded && !entry.error) {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(((actor.direction || 0) * Math.PI) / 180);
        ctx.drawImage(entry.img, -size / 2, -size / 2, size, size);
        ctx.restore();
      }

      if (actor.speechMessage) {
        const text = String(actor.speechMessage);
        ctx.save();
        ctx.font = "14px Arial";
        const padding = 6;
        const metrics = ctx.measureText(text);
        const bubbleW = metrics.width + padding * 2;
        const bubbleH = 24;
        const bx = x - bubbleW / 2;
        const by = y - size / 2 - bubbleH - 8;
        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#333333";
        ctx.lineWidth = 1;
        drawRoundedRect(ctx, bx, by, bubbleW, bubbleH, 6);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = "#222222";
        ctx.fillText(text, bx + padding, by + 16);
        ctx.restore();
      }
    });

    if (currentScene.heading?.text) {
      ctx.save();
      ctx.fillStyle = currentScene.heading.color || "#222";
      ctx.font = `bold ${currentScene.heading.size || 38}px Arial`;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(currentScene.heading.text, width / 2, 8);
      ctx.restore();
    }
  };

  const drawSimulatorFrame = (ctx, width, height) => {
    const sceneState = store.getState().scene;
    const currentScene = sceneState?.scenes?.[sceneState.currentSceneIndex];
    if (!currentScene) return;

    const bg = getCurrentSimulatorBackground() || "#ffffff";
    ctx.clearRect(0, 0, width, height);

    if (bg.startsWith("#")) {
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);
    } else {
      const entry = getImageCacheEntry(imageCacheRef.current, bg);
      if (entry?.loaded && !entry.error) {
        drawImageContain(ctx, entry.img, width, height);
      } else {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);
      }
    }

    const obstacles = currentScene.obstacles || [];
    obstacles.forEach((obstacle) => {
      const w = (obstacle.width || 50) * 0.4;
      const h = (obstacle.height || 50) * 0.4;
      const x = obstacle.x || 0;
      const y = obstacle.y || 0;
      ctx.save();
      ctx.translate(x + w / 2, y + h / 2);
      ctx.fillStyle = obstacle.color || "#4A9FD7";
      if (obstacle.shape === "circle") {
        ctx.beginPath();
        ctx.arc(0, 0, Math.min(w, h) / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (obstacle.shape === "triangle") {
        ctx.beginPath();
        ctx.moveTo(0, -h / 2);
        ctx.lineTo(w / 2, h / 2);
        ctx.lineTo(-w / 2, h / 2);
        ctx.closePath();
        ctx.fill();
      } else {
        ctx.fillRect(-w / 2, -h / 2, w, h);
      }
      ctx.restore();
    });

    const coloredAreas = currentScene.coloredAreas || [];
    coloredAreas.forEach((area) => {
      const w = (area.width || 60) * 0.4;
      const h = (area.height || 60) * 0.4;
      const x = area.x || 0;
      const y = area.y || 0;
      ctx.fillStyle = area.color || "#ffeb3b";
      ctx.fillRect(x, y, w, h);
    });

    const robots = sceneState.simulatorRobots || [];
    robots.forEach((robot) => {
      if (robot.visible === false) return;
      const entry = getImageCacheEntry(imageCacheRef.current, robot.image);
      if (!entry?.loaded || entry.error) return;
      const size = 20 * (robot.size || 1);
      const centerX = ((robot.x + 0.5) / simGridW) * width;
      const centerY = ((robot.y + 0.5) / simGridH) * height;
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(((robot.direction || 0) * Math.PI) / 180);
      ctx.drawImage(entry.img, -size / 2, -size / 2, size, size);
      ctx.restore();
    });
  };

  const handleRecordingComplete = async (blob) => {
    if (isUnmountingRef.current) return;
    const baseName = sanitizeRecordingName(scene?.heading?.text);
    setPendingRecordingBlob(blob);
    setPendingRecordingName(baseName);
    setShowRecordingSave(true);
  };

  const finalizeRecordingFromChunks = async (mimeTypeHint) => {
    if (stopHandledRef.current) return;
    stopHandledRef.current = true;
    try {
      const blob = new Blob(chunksRef.current, { type: mimeTypeHint || "video/webm" });
      chunksRef.current = [];
      if (!blob.size) {
        alert("Recording did not capture any frames. Please try again.");
        return;
      }
      await handleRecordingComplete(blob);
    } finally {
      cleanupRecording();
    }
  };

  const handleSaveRecording = async () => {
    if (!pendingRecordingBlob) return;
    setIsSaving(true);
    try {
      const name = sanitizeRecordingName(pendingRecordingName);
      await saveWebmFile(pendingRecordingBlob, name);
      setPendingRecordingBlob(null);
      setShowRecordingSave(false);
    } catch (err) {
      console.error("Recording save failed:", err);
      alert("Recording could not be saved.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardRecording = () => {
    setPendingRecordingBlob(null);
    setShowRecordingSave(false);
  };

  const stopRecording = () => {
    if (stopRequestedRef.current) return;
    stopRequestedRef.current = true;
    setIsRecording(false);
    isRecordingRef.current = false;
    if (window.__blockzieRecordingActive) {
      window.__blockzieRecordingActive = false;
    }

    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      try {
        if (typeof recorder.requestData === "function") {
          recorder.requestData();
        }
      } catch (err) {
        console.warn("Recording requestData failed:", err);
      }
      try {
        recorder.stop();
      } catch (err) {
        console.warn("Recording stop failed:", err);
        finalizeRecordingFromChunks(recorder.mimeType);
        return;
      }
      if (stopFallbackTimerRef.current) {
        clearTimeout(stopFallbackTimerRef.current);
      }
      stopFallbackTimerRef.current = setTimeout(() => {
        finalizeRecordingFromChunks(recorder.mimeType);
      }, 1500);
      return;
    }
    if (recorder) {
      finalizeRecordingFromChunks(recorder.mimeType);
      return;
    }
    cleanupRecording();
  };

  const startRecording = async ({ includeMic }) => {
    if (isRecordingRef.current || isSaving) return;

    const target = getRecordingTarget();
    if (!target) {
      alert("Stage area not found for recording.");
      return;
    }

    if (typeof MediaRecorder === "undefined") {
      alert("Screen recording is not supported in this browser.");
      return;
    }

    try {
      recordingTargetRef.current = target;
      recordingModeRef.current = simulatorOpen ? "simulator" : "stage";

      const rect = target.getBoundingClientRect();
      const width = Math.max(1, Math.round(target.offsetWidth || rect.width));
      const height = Math.max(1, Math.round(target.offsetHeight || rect.height));
      if (width < 2 || height < 2) {
        alert("Stage size is not ready for recording.");
        return;
      }
      recordSizeRef.current = { width, height };
      window.__blockzieRecordingActive = true;
      stopHandledRef.current = false;

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvasRef.current = canvas;
      const canvasHost = document.createElement("div");
      canvasHost.style.position = "fixed";
      canvasHost.style.left = "0";
      canvasHost.style.top = "0";
      canvasHost.style.width = `${width}px`;
      canvasHost.style.height = `${height}px`;
      canvasHost.style.opacity = "0";
      canvasHost.style.pointerEvents = "none";
      canvasHost.style.zIndex = "-1";
      canvasHost.appendChild(canvas);
      document.body.appendChild(canvasHost);
      canvasHostRef.current = canvasHost;

      const outputStream = canvas.captureStream(RECORDING_FPS);
      canvasStreamRef.current = outputStream;
      captureTrackRef.current = outputStream.getVideoTracks()[0] || null;
      if (!captureTrackRef.current) {
        alert("Recording video track is not available.");
        cleanupRecording();
        return;
      }

      const attachRecordingAudioTracks = () => {
        const stream = canvasStreamRef.current;
        const destination = recordingAudioDestinationRef.current;
        if (!stream || !destination) return;
        const sourceTracks = destination.stream.getAudioTracks();
        if (!sourceTracks.length) return;
        const existing = stream.getAudioTracks();
        sourceTracks.forEach((track) => {
          if (!existing.includes(track)) {
            stream.addTrack(track);
          }
        });
      };

      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        const audioContext = new AudioContextClass();
        audioContextRef.current = audioContext;
        const destination = audioContext.createMediaStreamDestination();
        recordingAudioDestinationRef.current = destination;
        window.__blockzieRecordingAudio = {
          context: audioContext,
          destination
        };
        window.__blockzieRecordingAttachAudio = attachRecordingAudioTracks;

        if (audioContext.state === "suspended") {
          await audioContext.resume();
        }

        if (includeMic) {
          const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          micStreamRef.current = micStream;
          const micSource = audioContext.createMediaStreamSource(micStream);
          micSource.connect(destination);
          attachRecordingAudioTracks();
        }
      } else if (includeMic) {
        const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        micStreamRef.current = micStream;
        micStream.getAudioTracks().forEach((track) => outputStream.addTrack(track));
      }

      const preferredMimeTypes = [
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm"
      ];
      const mimeType = preferredMimeTypes.find((type) => MediaRecorder.isTypeSupported(type));
      const recorderOptions = {
        videoBitsPerSecond: RECORDING_VIDEO_BITRATE,
        audioBitsPerSecond: RECORDING_AUDIO_BITRATE
      };
      if (mimeType) {
        recorderOptions.mimeType = mimeType;
      }
      const recorder = new MediaRecorder(outputStream, recorderOptions);

      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        await finalizeRecordingFromChunks(recorder.mimeType);
      };

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        alert("Recording canvas is not available.");
        cleanupRecording();
        return;
      }

      const drawFrame = () => {
        if (recordingModeRef.current === "simulator") {
          drawSimulatorFrame(ctx, width, height);
        } else {
          drawStageFrame(ctx, width, height);
        }
        if (captureTrackRef.current && typeof captureTrackRef.current.requestFrame === "function") {
          captureTrackRef.current.requestFrame();
        }
      };
      window.__blockzieRecordingDrawFrame = drawFrame;
      drawFrame();

      recorderRef.current = recorder;
      recorder.start(250);

      setIsRecording(true);
      isRecordingRef.current = true;
      stopRequestedRef.current = false;

      const frameInterval = 1000 / RECORDING_FPS;

      const renderLoop = (timestamp) => {
        if (!isRecordingRef.current) return;
        try {
          if (!lastFrameTimeRef.current || timestamp - lastFrameTimeRef.current >= frameInterval) {
            lastFrameTimeRef.current = timestamp;
            drawFrame();
          }
        } catch (err) {
          console.error("Recording render loop failed:", err);
        }
        renderLoopRef.current = requestAnimationFrame(renderLoop);
      };

      renderLoopRef.current = requestAnimationFrame(renderLoop);
    } catch (err) {
      console.error("Recording start failed:", err);
      alert("Recording failed to start.");
      cleanupRecording();
    }
  };

  const handleRecordButton = () => {
    if (isSaving) return;
    if (isRecording) {
      stopRecording();
      return;
    }
    setShowRecordingOptions(true);
  };
  const handleSimulatorToggle = () => {
    if (isAnimating) return;

    if (!simulatorOpen) {
      setIsAnimating(true);
      dispatch(setSimulatorOpenAction(true));

      setTimeout(() => {
        setIsAnimating(false);
      }, 400);

      if (onSimulator) {
        onSimulator();
      }
    } else {
      setIsAnimating(true);

      setTimeout(() => {
        dispatch(setSimulatorOpenAction(false));
        setIsAnimating(false);
      }, 400);
    }
  };

  const closeSimulator = () => {
    if (isAnimating) return;

    setIsAnimating(true);

    setTimeout(() => {
      dispatch(setSimulatorOpenAction(false));
      setIsAnimating(false);
    }, 400);
  };

  // SAFETY: clear any stuck animation lock after 1s.
  useEffect(() => {
    if (!isAnimating) return;
    const t = setTimeout(() => setIsAnimating(false), 1000);
    return () => clearTimeout(t);
  }, [isAnimating]);

  const updateStageRect = React.useCallback(() => {
    const stageEl =
      document.querySelector(".stage-wrapper") ||
      document.querySelector(".stage-area-section");

    if (stageEl) {
      const rect = stageEl.getBoundingClientRect();
      setStageRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      });
    }
  }, []);

  useEffect(() => {
    updateStageRect();
    window.addEventListener("resize", updateStageRect);
    return () => window.removeEventListener("resize", updateStageRect);
  }, [updateStageRect]);

  useEffect(() => {
    if (simulatorOpen) {
      updateStageRect();
    }
  }, [simulatorOpen, updateStageRect]);

  useEffect(() => {
    if (!simulatorOpen) return;
    const handleScroll = () => updateStageRect();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [simulatorOpen, updateStageRect]);

  useEffect(() => {
    if (!showViewDropdown) return;
    const handleClick = () => setShowViewDropdown(false);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [showViewDropdown]);

  useEffect(() => {
    if (simulatorOpen) {
      setShowViewDropdown(false);
    }
  }, [simulatorOpen]);

  useEffect(() => {
    return () => {
      dispatch(setSimulatorOpenAction(false));
    };
  }, [dispatch]);

  // Sync animation guard when simulatorOpen changes from external triggers (e.g., footer button)
  useEffect(() => {
    setIsAnimating(true);
    const t = setTimeout(() => setIsAnimating(false), 400);
    return () => clearTimeout(t);
  }, [simulatorOpen]);

  useEffect(() => {
    return () => {
      isUnmountingRef.current = true;
      if (recorderRef.current) {
        recorderRef.current.ondataavailable = null;
        recorderRef.current.onstop = null;
      }
      cleanupRecording();
    };
  }, []);

  const renderDefaultControls = () => (
    <>
      <button className="rp-btn" onClick={onFullScreen} title="Fullscreen">
        <img src="./assets/ui/FullOff.svg" alt="Fullscreen" />
      </button>
      <button className="rp-btn" onClick={onGridToggle} title="Grid Toggle">
        <img src="./assets/ui/gridOn.svg" alt="Grid" />
      </button>
      <button className="rp-btn" onClick={onHeading} title="Add Heading">
        <img src="./assets/ui/addText.svg" alt="Add Heading" />
      </button>
      <button
        className="rp-btn"
        onClick={() => setBackgroundModalOpen(true)}
        title="Choose Background"
      >
        <img src="./assets/ui/scene1.svg" alt="Background" />
      </button>
      <button
        className="rp-btn"
        onClick={handleDrawTailLine}
        title="Draw Tail Line"
      >
        <img src="./assets/ui/drawTailLine.svg" alt="Draw Tail Line" />
      </button>
      <button
        className="rp-btn"
        onClick={() => {
          const isStartWithGreenFlag =
            stageActor?.scripts?.some(b => b?.name === "Start on Green Flag");

          if (isStartWithGreenFlag) {
            handleGreenFlagClick();
          }
        }}
        title="Green Flag"
      >
        <img src="./assets/ui/go.svg" alt="Green Flag" />
      </button>
      <button
        className="rp-btn"
        title="Stop Script"
        onClick={() => {
          console.log("dY>` Stop Script clicked");
          stopRunningScript();
        }}
      >
        <img src="./assets/ui/stop1.svg" alt="Stop Script" />
      </button>
    </>
  );

  const renderSimulatorControls = () => (
    <>
      <button
        className="rp-btn"
        onClick={handleGreenFlagClick}
        title="Start on Green Flag"
      >
        <img src="./assets/ui/go.svg" alt="Green Flag" />
      </button>

      <button
        className="rp-btn"
        title="Stop Script"
        onClick={() => {
          console.log("dY>` Stop Script clicked");
          stopRunningScript();
        }}
      >
        <img src="./assets/ui/stop1.svg" alt="Stop Script" />
      </button>

      <button
        className="rp-btn"
        onClick={handleRecordButton}
        title={isRecording ? "Stop Recording" : "Record Stage"}
        disabled={isSaving}
      >
        <img
          src={isRecording ? "./assets/lib/recordon.svg" : "./assets/lib/recordoff.svg"}
          alt="Record"
        />
      </button>

      <button
        className="rp-btn"
        onClick={handleSimulatorBackgroundSelection}
        title="Simulator Background"
      >
        <img src="./assets/ui/backgrounds.png" alt="Simulator Background" />
      </button>

      {/* ADD THIS PAINT BUTTON */}
      <button className="rp-btn" onClick={handlePaintBackground} title="Paint Background">
        <img src="./assets/ui/paintbrush.png" alt="Paint" />
      </button>

      <button className="rp-btn" onClick={handleRobotSelection} title="Add Robot">
        <img src="./assets/ui/robot.png" alt="Add Robot" />
      </button>

      <button className="rp-btn" onClick={handleDrawTailLine} title="Draw Tail Line">
        <img src="./assets/ui/drawTailLine.svg" alt="Draw Tail Line" />
      </button>

      <button className="rp-btn" onClick={handleUploadBackground} title="Upload Background">
        <img src="./assets/ui/uploadBg.svg" alt="Upload Background" />
      </button>

      <button className="rp-btn" onClick={handleSaveProject} title="Save Project">
        <img src="./assets/ui/save.png" alt="Save Project" />
      </button>

      <button className="rp-btn" onClick={handleLoadProject} title="Load Project">
        <img src="./assets/ui/load.png" alt="Load Project" />
      </button>

      <button className="rp-btn" onClick={handleObstacle} title="Obstacle">
        <img src="./assets/ui/Obstacle.png" alt="Obstacle" />
      </button>

      <button className="rp-btn" onClick={handleColoredArea} title="Colored Area">
        <img src="./assets/ui/coloredArea.svg" alt="Colored Area" />
      </button>

      {/* <button
        className="rp-btn"
        onClick={handleSimulatorToggle}
        disabled={isAnimating}
        title="Close Simulator"
      >
        <img src="./assets/ui/simulator.png" alt="Close Simulator" />
      </button> */}
    </>
  );

  return (
    <>
      <div className="right-panel-controls">
        {simulatorOpen ? renderSimulatorControls() : (
          <>
            <div className="rp-dropdown">
              <button
                className="rp-btn rp-view-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowViewDropdown((v) => !v);
                }}
                title="View"
              >
                👁️
              </button>

              {showViewDropdown && (
                <div
                  className="rp-dropdown-panel"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="rp-dropdown-title">View</div>

                  <button
                    className="rp-dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowViewDropdown(false);
                      onFullScreen();
                    }}
                  >
                    <img src="./assets/ui/FullOff.svg" alt="Fullscreen" />
                    <span>Full Background</span>
                  </button>

                  <button
                    className="rp-dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowViewDropdown(false);
                      onGridToggle();
                    }}
                  >
                    <img src="./assets/ui/gridOn.svg" alt="Grid" />
                    <span>Show Grid in Stage Area</span>
                  </button>

                  <button
                    className="rp-dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowViewDropdown(false);
                      onHeading();
                    }}
                  >
                    <img src="./assets/ui/addText.svg" alt="Add Heading" />
                    <span>Heading</span>
                  </button>

                  <div className="rp-dropdown-divider" />

                  <button
                    className="rp-dropdown-close"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowViewDropdown(false);
                    }}
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
            <button
              className="rp-btn"
              onClick={() => setBackgroundModalOpen(true)}
              title="Choose Background"
            >
              <img src="./assets/ui/scene1.svg" alt="Background" />
            </button>
            <button
              className="rp-btn"
              onClick={handleDrawTailLine}
              title="Draw Tail Line"
            >
              <img src="./assets/ui/drawTailLine.svg" alt="Draw Tail Line" />
            </button>
            <button
              className="rp-btn"
              onClick={() => {
                const isStartWithGreenFlag =
                  stageActor?.scripts?.some(b => b?.name === "Start on Green Flag");

                if (isStartWithGreenFlag) {
                  handleGreenFlagClick();
                }
              }}
              title="Green Flag"
            >
              <img src="./assets/ui/go.svg" alt="Green Flag" />
            </button>
            <button
              className="rp-btn"
              title="Stop Script"
              onClick={() => {
                console.log("🛑 Stop Script clicked");
                stopRunningScript();
              }}
            >
              <img src="./assets/ui/stop1.svg" alt="Stop Script" />
            </button>

            <button
              className="rp-btn"
              onClick={handleRecordButton}
              title={isRecording ? "Stop Recording" : "Record Stage"}
              disabled={isSaving}
            >
              <img
                src={isRecording ? "./assets/lib/recordon.svg" : "./assets/lib/recordoff.svg"}
                alt="Record"
              />
            </button>

            {/* <button
              className="rp-btn"
              onClick={handleSimulatorToggle}
              disabled={isAnimating}
              title="Simulator"
            >
              <img src="./assets/ui/simulator.png" alt="Simulator" />
            </button> */}
          </>
        )}
      </div>

      {/* Sliding Simulator */}
      {simulatorOpen && (
        <>
          <div
            className={`simulator-partial-overlay ${simulatorOpen ? "active" : ""}`}
            onClick={closeSimulator}
            style={{
              ...(stageRect || {}),
              ...(showObstacleGallery ||
                showColoredAreaGallery ||
                showDrawLineModal ||
                backgroundModalOpen
                ? { pointerEvents: "none" }
                : null)
            }}
          />

          <div
            className={`simulator-partial-container ${simulatorOpen ? "open" : ""}`}
            style={stageRect || undefined}
          >
            {/* Content takes full height */}
            <div className="simulator-content" style={{ height: "100%" }}>
              {/* Stage */}
              <div className="simulator-stage-wrapper">
                <div
                  className="stage-area-section"
                  style={{ width: "100%", height: "100%", position: "relative" }}
                >
                  <React.Suspense
                    fallback={
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#222",
                          fontSize: "14px"
                        }}
                      >
                        Loading simulator...
                      </div>
                    }
                  >
                    <SimulatorModal onClose={closeSimulator} />
                  </React.Suspense>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      {backgroundModalOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 2000 }}>
          <React.Suspense
            fallback={
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(0,0,0,0.35)",
                  color: "white",
                  fontSize: "14px"
                }}
              >
                Loading backgrounds...
              </div>
            }
          >
            <BackgroundGallery
              open={true}
              onClose={() => setBackgroundModalOpen(false)}
              onBackgroundChange={onBackgroundChange}
            />
          </React.Suspense>
        </div>
      )}

      {showDrawLineModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 2000 }}>
          <DrawLineModal onClose={() => setShowDrawLineModal(false)} dispatch={dispatch} />
        </div>
      )}

      <RecordingOptionsModal
        open={showRecordingOptions}
        onCancel={() => setShowRecordingOptions(false)}
        onConfirm={(options) => {
          setShowRecordingOptions(false);
          startRecording(options);
        }}
        disabled={isSaving}
      />

      <RecordingSaveModal
        open={showRecordingSave}
        name={pendingRecordingName}
        onNameChange={setPendingRecordingName}
        onSave={handleSaveRecording}
        onDiscard={handleDiscardRecording}
        saving={isSaving}
      />

      {showObstacleGallery && (
        <div style={{ position: "fixed", inset: 0, zIndex: 2000 }}>
          <React.Suspense
            fallback={
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(0,0,0,0.35)",
                  color: "white",
                  fontSize: "14px"
                }}
              >
                Loading obstacles...
              </div>
            }
          >
            <ObstacleGallery open={true} onClose={() => setShowObstacleGallery(false)} />
          </React.Suspense>
        </div>
      )}

      {showColoredAreaGallery && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000 }}>
          <React.Suspense
            fallback={
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(0,0,0,0.35)",
                  color: "white",
                  fontSize: "14px"
                }}
              >
                Loading colored areas...
              </div>
            }
          >
            <ColoredAreaGallery open={true} onClose={() => setShowColoredAreaGallery(false)} />
          </React.Suspense>
        </div>
      )}

      {/* ADD PAINT EDITOR HERE */}
      {showPaintEditor && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2001,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <React.Suspense
            fallback={
              <div
                style={{
                  padding: "12px 16px",
                  background: "white",
                  borderRadius: "8px",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
                  fontSize: "14px"
                }}
              >
                Loading paint editor...
              </div>
            }
          >
            <PaintEditor
              onSave={handlePaintSave}
              onClose={() => setShowPaintEditor(false)}
              mode="background"
            />
          </React.Suspense>
        </div>
      )}
    </>
  );
}








